import * as React from 'react';
import { useStyles } from '../../style/Pages/BasicCompanyInfo';
import { Box } from '@mui/system';
import { useFormik } from 'formik';
import { Button, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import { schemaValidationCompanyBankAccountInfo, schemaValidationCompanyBankAccount } from '../../utils/validation';
import { InputField } from '../../components/InputController';
import { useDispatch, useSelector } from 'react-redux';
import { clearOnboardingApplicationFlags, getPmDetails, getUpdateOnboardingApplication, selectOnboardingApplicationState } from './sagas/pmonboardingSlice';
import { useHistory } from 'react-router-dom';
import { CustomDropZone } from './components/CustomDropZone';
import { uploadDocuments, selectDocumentState } from '../../shared/sagas/document/documentSlice';
import BackIcon from '../../icons/backIcon.svg';

export const CompanyBankAccount = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { pmDetails, onboarding, isFetching, isSuccess } = useSelector(selectOnboardingApplicationState);

  //Company Bank Account Saved Values
  const [savedValues, setSavedValues] = React.useState<schemaValidationCompanyBankAccountInfo>({
    bank_name: '',
    routing_number: '',
    account_number: '',
    bank_files: [],
  });

  React.useEffect(() => {
    onboarding?.BankAccount?.BankName &&
      setSavedValues({
        ...savedValues,
        bank_name: onboarding?.BankAccount?.BankName,
        routing_number: onboarding?.BankAccount?.RoutingNumber,
        account_number: onboarding?.BankAccount?.AccountNumber,
        bank_files: onboarding?.BankAccount?.BankFiles,
      });
  }, [onboarding]);

  const [isFormSubmitted, setIsFormSubmitted] = React.useState(false);

  const formik = useFormik<schemaValidationCompanyBankAccountInfo>({
    initialValues: savedValues,
    validationSchema: schemaValidationCompanyBankAccount,
    onSubmit: async (values) => {
      dispatch({
        type: getUpdateOnboardingApplication.type,
        payload: {
          company_uuid: onboarding?.Uuid || pmDetails?.Company?.Uuid,
          data: {
            BankAccount: {
              EntityUuid: onboarding?.BankAccount?.EntityUuid,
              BankName: values.bank_name,
              RoutingNumber: values.routing_number,
              AccountNumber: values.account_number,
            },
          },
        },
      });
      setIsFormSubmitted(true);
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  React.useEffect(() => {
    if (isSuccess && isFormSubmitted && onboarding?.BankAccount?.EntityUuid) {
      //Filter newly added bank files
      const uploadingFiles = formik.values.bank_files?.filter((f: any) => f.name);
      if (uploadingFiles?.length) {
        dispatch({
          type: uploadDocuments.type,
          payload: {
            belongsToEntityUuid: onboarding.BankAccount.EntityUuid,
            documents: uploadingFiles,
            documentType: 'BankAccountFile',
          },
        });
      } else {
        // Clear all onboarding flags
        dispatch({
          type: clearOnboardingApplicationFlags.type,
          payload: {},
        });

        history.push('/onboarding/applicationwizard');
      }
    }
  }, [isSuccess, isFormSubmitted, onboarding]);

  const { isUploadingDocuments, isUploadingDocumentsSuccess, isUploadingDocumentsFailed, documentErrorMessage, uploadDocumentResponse: documentResponse } = useSelector(selectDocumentState);
  const [bankGlobalError, setBankGlobalError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isUploadingDocumentsSuccess) {
      setBankGlobalError('');

      if (documentResponse?.some((d) => d.remoteUuid)) {
        //! this doesn't seem right. We should probably return an error instead of a 400
        dispatch({
          type: getUpdateOnboardingApplication.type,
          payload: {
            company_uuid: onboarding?.Uuid || pmDetails?.Company?.Uuid,
            data: {},
          },
        });
      } else {
        setBankGlobalError('Uploading documents failed. Please try again later or contact support.');
      }
    }
    if (isUploadingDocumentsFailed) {
      setBankGlobalError(documentErrorMessage.message);
    }
  }, [isUploadingDocumentsSuccess, isUploadingDocumentsFailed, documentErrorMessage]);

  React.useEffect(() => {
    // Clear all onboarding flags
    dispatch({
      type: clearOnboardingApplicationFlags.type,
      payload: {},
    });

    setBankGlobalError('');

    // fetch company uuid if onboarding state has been erased, probably due to a refresh
    !onboarding?.Uuid &&
      dispatch({
        type: getPmDetails.type,
        payload: {},
      });
  }, []);

  React.useEffect(() => {
    // fetch onboarding application
    pmDetails.Company?.Uuid &&
      dispatch({
        type: getUpdateOnboardingApplication.type,
        payload: {
          company_uuid: pmDetails.Company.Uuid,
          data: {},
        },
      });
  }, [pmDetails]);

  const handleFiles = (acceptedFiles: File[]) => {
    let files = formik.values.bank_files || [];

    if (files.length + acceptedFiles.length <= 3) {
      acceptedFiles.forEach((af: File) => {
        if (!files.find((f) => f.name === af.name)) {
          files = [...files, af];
        }
      });
      formik.setFieldValue('bank_files', files);
    }
  };

  const handleDelete = (file: File) => {
    const files = formik.values.bank_files || [];
    formik.setFieldValue(
      'bank_files',
      files.filter((f) => f.name !== file.name),
    );
  };

  const handleClickFinishLater = () => {
    history.push('/onboarding/applicationwizard');
  };

  return (
    <Grid item xs={11} sm={9} lg={8} xl={7}>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <IconButton sx={{ p: 1 }} onClick={handleClickFinishLater}>
          <img src={BackIcon} alt="cancel icon" width="17px" height="17px" />
        </IconButton>
        <Typography
          style={{
            fontWeight: 600,
            fontSize: '18px',
          }}
          sx={{ p: 1 }}
          className={classes.companyInfo}
        >
          Company Bank Account
        </Typography>
      </Box>

      {bankGlobalError && (
        <Typography
          style={{
            fontFamily: 'Work Sans',
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '12px',
            lineHeight: '18px',
            color: '#DB3939',
            textAlign: 'center',
            borderTop: '2px solid #DB3939',
            borderRadius: '24px',
            padding: '12px 20px',
            margin: '20px 20px 10px',
          }}
        >
          {bankGlobalError}
        </Typography>
      )}

      <Typography
        style={{
          fontSize: '16px',
          lineHeight: '24px',
          fontFamily: 'Work Sans',
          color: '#6B7280',
          margin: '2px 18px 24px',
        }}
      >
        Note: A bank account in your company’s name is requried for onboarding and underwriting. Additional rental and/or escrow accounts are added later via the integration or property set up.
      </Typography>
      <Box component="form" noValidate onSubmit={formik.handleSubmit} className={classes.centerLabel}>
        <Box style={{ margin: '5px 0' }}>
          <InputField
            name={'bank_name'}
            placeholder={'Bank Name'}
            label={'Bank Name'}
            isError={false}
            errors={formik.errors?.bank_name}
            touched={formik.touched?.bank_name}
            onBlur={formik.handleBlur}
            onChange={(e) => formik.setFieldValue('bank_name', e.target.value)}
            value={formik.values.bank_name || ''}
          />
        </Box>
        <Box style={{ margin: '5px 0' }}>
          <InputField
            name={'routing_number'}
            placeholder={'Routing Number'}
            label={'Routing Number'}
            isError={false}
            errors={formik.errors?.routing_number}
            touched={formik.touched?.routing_number}
            onBlur={formik.handleBlur}
            onChange={(e) => formik.setFieldValue('routing_number', e.target.value)}
            value={formik.values.routing_number || ''}
          />
        </Box>
        <Box style={{ margin: '5px 0' }}>
          <InputField
            name={'account_number'}
            placeholder={'Account Number'}
            label={'Account Number'}
            isError={false}
            errors={formik.errors?.account_number}
            touched={formik.touched?.account_number}
            onBlur={formik.handleBlur}
            onChange={(e) => formik.setFieldValue('account_number', e.target.value)}
            value={formik.values.account_number || ''}
          />
        </Box>

        <Typography
          style={{
            fontWeight: 600,
            fontSize: '18px',
            paddingTop: '0px',
          }}
          sx={{ p: 2 }}
          className={classes.companyInfo}
        >
          Upload void check or bank letter
        </Typography>

        <Box sx={{ marginBottom: '24px' }}>
          {/* maxFileSize must be in MB, example: maxFileSize={20} */}
          <CustomDropZone
            files={formik?.values?.bank_files || []}
            fileLimit={3}
            handleFilesAdded={handleFiles}
            handleFileDeleted={handleDelete}
            maxFileSize={20}
            description="Up to 20MB per file. Up to 3 Files at a time."
          />
          <Typography style={{ fontFamily: 'Work Sans', fontSize: '14px', marginTop: '10px' }}>{'Supported File Types: .jpg, .pdf , .png'}</Typography>
        </Box>

        <Box style={{ width: '100%' }} sx={{ display: 'flex', justifyContent: 'end' }}>
          <div className={classes.wrapper}>
            <Button type="submit" disabled={Boolean(!formik.isValid)} className={classes.signUpBtn}>
              <Typography className={classes.signUpBtnText} sx={{ margin: '0 10px' }}>
                Save
              </Typography>
            </Button>
            {isFormSubmitted && (isFetching || isUploadingDocuments) && <CircularProgress size={26} className={classes.buttonProgress} />}
          </div>
        </Box>
      </Box>
    </Grid>
  );
};
